if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoom = 1;
        this.zoomStatus = this.querySelector('[data-media-zoom-status]');
        this.mediaContainer = this.querySelector('[role="document"]');
        this.mediaContainer.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
        this.mediaContainer.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
        this.mediaContainer.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
        this.mediaContainer.addEventListener('touchend', this.handleTouchEnd.bind(this));
        this.mediaContainer.addEventListener('touchcancel', this.handleTouchEnd.bind(this));
        window.addEventListener('resize', () => this.updateZoomedImage());
      }

      hide() {
        this.setZoom(1);
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        this.activeMedia = activeMedia;
        this.mediaContainer.classList.toggle('is-image-active', activeMedia.matches('img'));
        this.setZoom(1);

        const container = this.querySelector('[role="document"]');

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      setZoom(zoom, origin) {
        const previousZoom = this.zoom;
        const nextZoom = Math.min(3, Math.max(1, zoom));
        const rect = this.mediaContainer.getBoundingClientRect();
        const anchor = origin
          ? {
              previousZoom,
              x: origin.clientX - rect.left,
              y: origin.clientY - rect.top,
              scrollLeft: this.mediaContainer.scrollLeft,
              scrollTop: this.mediaContainer.scrollTop,
            }
          : null;

        this.zoom = nextZoom;
        this.updateZoomedImage(anchor);
      }

      updateZoomedImage(anchor) {
        const container = this.mediaContainer;
        const image = this.activeMedia?.matches('img') ? this.activeMedia : null;

        if (!image || !container) {
          container?.classList.remove('is-zoomed');
          return;
        }

        if (!image.complete) {
          image.addEventListener('load', () => this.updateZoomedImage(anchor), { once: true });
          return;
        }

        if (!image.naturalWidth || !image.naturalHeight) return;

        const styles = getComputedStyle(container);
        const availableWidth =
          container.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
        const availableHeight =
          container.clientHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom);
        const fit = Math.min(availableWidth / image.naturalWidth, availableHeight / image.naturalHeight);

        image.style.width = `${Math.round(image.naturalWidth * fit * this.zoom)}px`;
        image.style.height = `${Math.round(image.naturalHeight * fit * this.zoom)}px`;
        container.classList.toggle('is-zoomed', this.zoom > 1);
        if (this.zoomStatus) this.zoomStatus.textContent = `${Math.round(this.zoom * 100)}%`;

        requestAnimationFrame(() => {
          if (anchor && anchor.previousZoom > 0) {
            const ratio = this.zoom / anchor.previousZoom;
            container.scrollLeft = (anchor.scrollLeft + anchor.x) * ratio - anchor.x;
            container.scrollTop = (anchor.scrollTop + anchor.y) * ratio - anchor.y;
          } else {
            container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
            container.scrollTop = (container.scrollHeight - container.clientHeight) / 2;
          }
        });
      }

      handleWheel(event) {
        if (!this.activeMedia?.matches('img')) return;
        event.preventDefault();
        this.setZoom(this.zoom + (event.deltaY < 0 ? 0.25 : -0.25), event);
      }

      handleTouchStart(event) {
        if (!this.activeMedia?.matches('img')) return;

        if (event.touches.length === 2) {
          event.preventDefault();
          this.pinchStartDistance = this.touchDistance(event.touches);
          this.pinchStartZoom = this.zoom;
          this.panStart = null;
        } else if (event.touches.length === 1 && this.zoom > 1) {
          const touch = event.touches[0];
          this.panStart = {
            x: touch.clientX,
            y: touch.clientY,
            scrollLeft: this.mediaContainer.scrollLeft,
            scrollTop: this.mediaContainer.scrollTop,
          };
        }
      }

      handleTouchMove(event) {
        if (event.touches.length === 2 && this.pinchStartDistance) {
          event.preventDefault();
          const midpoint = {
            clientX: (event.touches[0].clientX + event.touches[1].clientX) / 2,
            clientY: (event.touches[0].clientY + event.touches[1].clientY) / 2,
          };
          this.setZoom(
            this.pinchStartZoom * (this.touchDistance(event.touches) / this.pinchStartDistance),
            midpoint
          );
        } else if (event.touches.length === 1 && this.panStart && this.zoom > 1) {
          event.preventDefault();
          const touch = event.touches[0];
          this.mediaContainer.scrollLeft = this.panStart.scrollLeft - (touch.clientX - this.panStart.x);
          this.mediaContainer.scrollTop = this.panStart.scrollTop - (touch.clientY - this.panStart.y);
        }
      }

      handleTouchEnd(event) {
        if (event.touches.length < 2) this.pinchStartDistance = null;
        if (event.touches.length === 1 && this.zoom > 1) {
          const touch = event.touches[0];
          this.panStart = {
            x: touch.clientX,
            y: touch.clientY,
            scrollLeft: this.mediaContainer.scrollLeft,
            scrollTop: this.mediaContainer.scrollTop,
          };
        } else if (event.touches.length === 0) {
          this.panStart = null;
        }
      }

      touchDistance(touches) {
        return Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
      }
    }
  );
}
